import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { VideoCard } from '../components/VideoCard';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VideosPage() {
  const [allVideos, setAllVideos] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [brandSeries, setBrandSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const [allRes, podcastsRes, seriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/videos?pageSize=20`),
          axios.get(`${API_URL}/api/videos?series=podcast&pageSize=20`),
          axios.get(`${API_URL}/api/videos?series=brand-series&pageSize=20`),
        ]);

        setAllVideos(allRes.data);
        setPodcasts(podcastsRes.data);
        setBrandSeries(seriesRes.data);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const renderSkeletons = (count) => {
    return Array.from({ length: count }).map((_, i) => (
      <div key={i} className="space-y-3">
        <Skeleton className="w-full aspect-[16/9] rounded-lg" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    ));
  };

  return (
    <>
      <Helmet>
        <title>Videos · B&B Magazine</title>
        <meta name="description" content="Watch podcasts, brand series, and video content from B&B Magazine." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com/videos" />
      </Helmet>

      <HeaderNav />

      <main>
        <section className="w-full py-12 md:py-16">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                Watch
              </span>
              <h1 className="mt-2 font-display text-4xl md:text-5xl font-bold mb-4">
                Videos
              </h1>
              <p className="text-muted-foreground text-lg max-w-[65ch]">
                Podcasts, brand series, and video interviews exploring the creator economy.
              </p>
            </motion.div>

            {/* Tabs for filtering */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-8 bg-[hsl(var(--surface-2))] p-1">
                <TabsTrigger value="all" className="font-ui">All Videos</TabsTrigger>
                <TabsTrigger value="podcasts" className="font-ui">Podcasts</TabsTrigger>
                <TabsTrigger value="series" className="font-ui">Brand Series</TabsTrigger>
              </TabsList>

              {/* All Videos */}
              <TabsContent value="all">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderSkeletons(6)}
                  </div>
                ) : allVideos.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {allVideos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No videos found.</p>
                  </div>
                )}
              </TabsContent>

              {/* Podcasts */}
              <TabsContent value="podcasts">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderSkeletons(6)}
                  </div>
                ) : podcasts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {podcasts.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No podcasts found.</p>
                  </div>
                )}
              </TabsContent>

              {/* Brand Series */}
              <TabsContent value="series">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderSkeletons(6)}
                  </div>
                ) : brandSeries.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {brandSeries.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No brand series found.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}